import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { GENERIC_CREDENTIALS_ERROR, GENERIC_NETWORK_ERROR, normalizeError } from '../../lib/errors';
import { Button, Notice } from '../../components/primitives';
import { CheckboxField, PasswordField, TextField } from '../../components/form';
import { AuthShell } from './AuthShell';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Ingrese su correo institucional.')
    .email('El correo no tiene un formato válido.'),
  password: z.string().min(1, 'Ingrese su contraseña.'),
  rememberMe: z.boolean(),
});

type LoginForm = z.infer<typeof loginSchema>;

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const { user, login } = useAuth();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  if (user !== null) {
    const state = location.state as LocationState | null;
    return <Navigate to={user.mustChangePassword ? '/cambiar-password' : (state?.from ?? '/')} replace />;
  }

  const onSubmit = async (values: LoginForm): Promise<void> => {
    setFormError(null);
    try {
      await login({
        email: values.email.trim(),
        password: values.password,
        rememberMe: values.rememberMe,
      });
    } catch (error) {
      const normalized = normalizeError(error);
      /**
       * Mensaje SIEMPRE genérico ante credenciales.
       *
       * Distinguir «el correo no existe» de «la contraseña es incorrecta»
       * convertiría el formulario en un verificador de cuentas. Sólo se
       * diferencian los casos que no hablan de credenciales: red caída, bloqueo
       * temporal y exceso de intentos.
       */
      if (normalized.code === 'NETWORK') setFormError(GENERIC_NETWORK_ERROR);
      else if (normalized.statusCode === 423)
        setFormError('La cuenta está bloqueada temporalmente. Intente más tarde o contacte al administrador.');
      else if (normalized.statusCode === 429)
        setFormError('Demasiados intentos. Espere unos minutos antes de volver a intentarlo.');
      else setFormError(GENERIC_CREDENTIALS_ERROR);
    }
  };

  return (
    <AuthShell
      title="Iniciar sesión"
      subtitle="Acceda con su correo institucional. Si no recuerda su contraseña puede solicitar el restablecimiento."
    >
      <form className="auth__form" onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        {formError !== null && (
          <Notice tone="error" role="alert">
            {formError}
          </Notice>
        )}

        <TextField
          label="Correo institucional"
          type="email"
          autoComplete="username"
          autoFocus
          placeholder="nombre.apellido@oxxean.cl"
          required
          error={errors.email?.message}
          {...register('email')}
        />

        <PasswordField
          label="Contraseña"
          autoComplete="current-password"
          required
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="auth__row">
          <CheckboxField label="Recordar sesión" {...register('rememberMe')} />
          <Link to="/recuperar-password" className="tiny">
            ¿Olvidó su contraseña?
          </Link>
        </div>

        <Button type="submit" variant="primary" block loading={isSubmitting}>
          {isSubmitting ? 'Verificando…' : 'Ingresar'}
        </Button>

        <p className="tiny muted">
          Uso interno de OXXEAN. Cada inicio de sesión queda registrado con fecha, dirección IP y
          agente del navegador.
        </p>
      </form>
    </AuthShell>
  );
}
